import React, { useState, useEffect, useRef, useContext } from 'react'; 

import { useStepContext } from "../utils/StepContext.jsx"
import { useFormContext } from "../utils/FormContext.jsx"
import { AddressValueContext, AddressValueProvider } from "../utils/AddressContext.jsx";
import { useMediaQuery } from 'react-responsive';
import { useAuth } from '../utils/AuthContext/AuthContext.jsx';

import MainCard from "./MainCard.jsx";
import ProgressBar from './ProgressBar/ProgressBar.jsx';
import SaveButton from './SaveButton.jsx';
import PDFPreview from './PDFPreview.jsx';
import HelpButton from './HelpButton.jsx';
import ShishitoAssist from './ShishitoAssist/ShishitoAssist.jsx';

const NameCard = () => {

    const { handleNext, handleBack } = useStepContext();
    const { formData, handleInputChange, handleBuyerAddressChange, formId } = useFormContext(); 
    const { nameCardAddress, setNameCardAddress } = useContext(AddressValueContext); 
    const [isAddressSelected, setIsAddressSelected] = useState(false);  
    const autocompleteInputRef = useRef(null);
    const isMobile = useMediaQuery({ query: '(max-width: 768px)' });
    const { token, user } = useAuth();
    const [isAssistVisible, setIsAssistVisible] = useState(false);

    const currentPageValue = 0;
    const currentSubStepProgress = 1/5; 

    const [errors, setErrors] = useState({
        lastName: false, 
        firstName: false,
        buyer1HomeAddress: false,  
        buyer1Email: false, 
        buyer1Phone: false
    }); 

    useEffect(() => {
        if (formData.buyer1HomeAddress) {
            setNameCardAddress(prevNameCardAddress => {
                const newNameCardAddress = Object.values(formData.buyer1HomeAddress).join(', ');
                return newNameCardAddress;
            });
            setIsAddressSelected(true); // Set to true when address is pre-loaded
        }
    }, [formData.buyer1HomeAddress]); // Only run when buyer1HomeAddress changes

    // Validate inputs
    const validateFields = () => {
        const newErrors = {};
        newErrors.lastName = !formData.lastName;  // true if lastName is empty
        newErrors.firstName = !formData.firstName;  // true if firstName is empty
        newErrors.buyer1HomeAddress = !formData.buyer1HomeAddress || (!isAddressSelected && !formData.buyer1HomeAddress);  // Check if address exists and is selected
        newErrors.buyer1Email = !formData.buyer1Email;  // true if email is empty
        newErrors.buyer1Phone = !formData.buyer1Phone;  // true if phone is empty
        setErrors(newErrors);

        // Proceed only if all fields are filled
        return !newErrors.lastName && !newErrors.firstName && !newErrors.buyer1Email && !newErrors.buyer1Phone && !newErrors.buyer1HomeAddress; 
    };

    const onNext = () => {
        if (validateFields()) {
            handleNext();
        } else {
            alert("Please enter valid names in all required fields, or enter a valid address from the dropdown.");
        }
    };

    const toggleAssist = () => {
        setIsAssistVisible(!isAssistVisible);
    };

    useEffect(() => {
    const autocomplete = new google.maps.places.Autocomplete(autocompleteInputRef.current);

    autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        const buyer1HomeAddress = {
            street: '',
            city: '',
            state: '',
            zip: ''
        };
        for (const component of place.address_components) {
            const componentType = component.types[0];
            switch (componentType) {
                case 'street_number': {
                    buyer1HomeAddress.street = `${component.long_name} ${buyer1HomeAddress.street}`;
                    break;
                }
                case 'route': {
                    buyer1HomeAddress.street += component.short_name;
                    break;
                }
                case 'locality':
                    buyer1HomeAddress.city = component.long_name;
                    break;
                case 'administrative_area_level_1': {
                    buyer1HomeAddress.state = component.short_name;
                    break;
                }
                case 'postal_code':
                    buyer1HomeAddress.zip = component.short_name;
                    break;
            }
        }
        handleBuyerAddressChange({
            target: {
                name: 'buyer1HomeAddress',
                value: buyer1HomeAddress
            }
        });
        setNameCardAddress(Object.values(buyer1HomeAddress).join(', ')); // Add this line
        setIsAddressSelected(true); // Address selected from autocomplete
    });
}, []);

    return (
        <div className="w-full flex flex-col items-center">
            {/* <ProgressBar currentPage={currentPageValue} subStepProgress={currentSubStepProgress}/> */}
            <div className="w-full h-full justify-start items-center flex flex-col">
                <MainCard width={750} maxHeight={450} gap={0}>
                <div className="overflow-y-auto w-full">
                    <div className='flex flex-col gap-5'>
                    <div className='inline-flex justify-between items-center'>
                        {isMobile ? <h3 className="text-black text-[18px] md:text-xl text-left font-normal font-['Inter']">Enter details</h3> : <h3 className="text-black text-[16px] md:text-xl text-left font-normal font-['Inter']">Lets start with your information</h3> }
                        <div className='md:h-auto gap-2 md:gap-4 inline-flex items-center'>
                            <HelpButton onClick={toggleAssist}/>
                            <SaveButton formData={formData} formId={formId}/>
                            {user && <PDFPreview formData={formData}/> } 
                        </div>  
                    </div>
                    <div className="w-full justify-between gap-2 items-start flex flex-col">
                        <span className='text-sm text-slate-600'>
                            Full Name
                        </span>
                        <input 
                            type="text"
                            name="firstName"
                            placeholder="First Name *" 
                            className={`py-1 px-3 h-[39px] rounded-md border ${errors.lastName ? 'border-red-500 bg-red-100' : 'border-slate-300'} border-slate-300 w-full`} 
                            value={formData.firstName}
                            onChange={handleInputChange}
                            required
                        >    
                        </input>
                        <input 
                            type="text"
                            name="lastName"
                            placeholder="Last Name *" 
                            className={`py-1 px-3 h-[39px] rounded-md border ${errors.lastName ? 'border-red-500 bg-red-100' : 'border-slate-300'} border-slate-300 w-full`}
                            value={formData.lastName}
                            onChange={handleInputChange}
                            required
                        >    
                        </input>
                    </div>
                    <div className="w-full justify-between gap-2 items-start flex flex-col">
                        <span className='text-sm text-slate-600'>
                            Home Address
                        </span>
                        <input 
                            ref={autocompleteInputRef}
                            type="text"
                            name="buyer1HomeAddress"
                            placeholder="Address *" 
                            className={`py-1 px-3 h-[39px] rounded-md border ${errors.buyer1HomeAddress ? 'border-red-500 bg-red-100' : 'border-slate-300'} border-slate-300 w-full`} 
                            value={nameCardAddress}
                            onChange={(event) => {
                                setNameCardAddress(event.target.value);
                                setIsAddressSelected(false); // Reset if user types manually
                            }}
                            required
                        >    
                        </input>
                    </div>
                    <div className="w-full justify-between gap-2 items-start flex flex-col">
                        <span className='text-sm text-slate-600'>
                            Phone
                        </span>
                        <input 
                            type="text"
                            name="buyer1Phone"
                            placeholder="Phone *" 
                            className={`py-1 px-3 h-[39px] rounded-md border ${errors.buyer1Phone ? 'border-red-500 bg-red-100' : 'border-slate-300'} border-slate-300 w-full`} 
                            value={formData.buyer1Phone}
                            onChange={handleInputChange}
                            required
                        >    
                        </input>
                    </div>
                    <div className="w-full justify-between gap-2 items-start flex flex-col">
                        <span className='text-sm text-slate-600'>
                            Email
                        </span>
                        <input 
                            type="text"
                            name="buyer1Email"
                            placeholder="Email *" 
                            className={`py-1 px-3 h-[39px] rounded-md border ${errors.buyer1Email ? 'border-red-500 bg-red-100' : 'border-slate-300'} border-slate-300 w-full`}  
                            value={formData.buyer1Email}
                            onChange={handleInputChange}
                            required
                        >    
                        </input>
                    </div>
                </div>
                </div>

                    <div className="flex w-full justify-center gap-10 md:gap-36 mt-2 bg-white">
                        <button className="w-[100px] md:w-[150px] justify-center text-center text-shi-green bg-white border-white text-[16px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-slate-200 items-center md:items-start flex" onClick={handleBack}>
                            Back
                        </button>
                        <button className="w-[100px] md:w-[150px] justify-center items-center text-center text-white bg-shi-green text-[16px] md:text-lg font-normal font-['Inter'] p-1.5 rounded-md border hover:bg-green-950 md:items-start flex" onClick={onNext}>
                            Next
                        </button>
                    </div>
                </MainCard>
                {isAssistVisible && (
                <div className="absolute bottom-1 md:right-4">
                    <ShishitoAssist onClose={toggleAssist}/>
                </div>
            )}
                </div>
        </div>
    )
}

export default NameCard; 